import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  TextInput,
  Alert,
  Select,
  Datepicker,
} from "flowbite-react";
import { useEffect, useState } from "react";
import { SquareX } from "lucide-react";
import axiosInstance from "../config/config";

export function ModalFrom({ closeModal, e_id = 0, fetchjobs }) {
  const [j_name, setJName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [s_date, setSDate] = useState(null);
  const [e_date, setEDate] = useState(null);
  const [status, setStatus] = useState("Initial");
  const [clients, setClients] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [t_color, setTColor] = useState("failure");
  const [msg, setMsg] = useState("");
  const [adis, setAdis] = useState(false);
  const [msg_n, setMsg_n] = useState("");

  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await axiosInstance.post("", {
          SysID: `select id, c_name from dbo.job_client;`,
        });
        setClients(response.data || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (e_id) {
      const fetchJob = async () => {
        try {
          const response = await axiosInstance.post("", {
            SysID: `select * from dbo.job where id=${e_id};`,
          });
          const job = response.data[0];
          setJName(job.j_name);
          setDescription(job.description || "");
          setSelectedClient(job.c_name);
          setSDate(job.s_date ? new Date(job.s_date) : null);
          setEDate(job.e_date ? new Date(job.e_date) : null);
          setStatus(job.status || "Initial");
        } catch (error) {
          console.error("Error fetching job:", error);
        }
      };
      fetchJob();
    }
  }, [e_id]);

  const showAlert = (color, title, message) => {
    setTColor(color);
    setMsg_n(title);
    setMsg(message);
    setAdis(true);
    setTimeout(() => setAdis(false), 3000);
  };

  const formatDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d.toISOString().split("T")[0];
    return d;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!j_name.trim()) {
      showAlert("failure", "Error!", "Job name is required!");
      return;
    }
    if (!selectedClient.trim()) {
      showAlert("failure", "Error!", "Client is required!");
      return;
    }

    if (status === "Completed" && e_id) {
      try {
        const check = await axiosInstance.post("", {
          SysID: `select count(*) as cnt from dbo.job_job_item where job_id=${e_id} and is_returned=0;`,
        });
        const cnt = check.data[0]?.cnt || 0;
        if (cnt > 0) {
          showAlert("failure", "Cannot Complete!", `${cnt} item(s) not yet returned.`);
          return;
        }
      } catch (error) {
        console.error("Error checking items:", error);
      }
    }

    try {
      const startDate = formatDate(s_date);
      const endDate = formatDate(e_date);
      const sdVal = startDate ? `'${startDate}'` : "NULL";
      const edVal = endDate ? `'${endDate}'` : "NULL";

      if (e_id) {
        await axiosInstance.post("", {
          SysID: `EXEC dbo.up_job @id=${e_id}, @j_name='${j_name}', @description='${description}', @c_name='${selectedClient}', @s_date=${sdVal}, @e_date=${edVal}, @status='${status}'; SELECT 1`,
        });
        showAlert("green", "Success!", "Job updated.");
      } else {
        await axiosInstance.post("", {
          SysID: `EXEC dbo.add_job @j_name='${j_name}', @description='${description}', @c_name='${selectedClient}', @s_date=${sdVal}, @e_date=${edVal}, @status='${status}'; SELECT 1`,
        });
        showAlert("green", "Success!", "Job saved.");
        setJName("");
        setDescription("");
        setSelectedClient("");
        setSDate(null);
        setEDate(null);
        setStatus("Initial");
      }
      fetchjobs();
    } catch (error) {
      console.error("Error saving job:", error);
      showAlert("failure", "Error!", "Failed to save.");
    }
  };

  return (
    <Modal show={true} size="md" onClose={closeModal} popup>
      <ModalHeader />
      <ModalBody>
        <div className="space-y-4">
          <div className="flex">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white">
              {e_id ? "Edit Job" : "Add Job"}
            </h3>
            <SquareX
              onClick={closeModal}
              className="ml-auto text-red-600 hover:cursor-pointer"
            />
          </div>

          <div>
            <div className="mb-2 block"><Label>Job Name</Label></div>
            <TextInput
              value={j_name}
              onChange={(e) => setJName(e.target.value)}
              placeholder="Job name"
              required
            />
          </div>

          <div>
            <div className="mb-2 block"><Label>Description</Label></div>
            <TextInput
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description (optional)"
            />
          </div>

          <div className="relative">
            <div className="mb-2 block"><Label>Client</Label></div>
            <TextInput
              placeholder="Search client..."
              value={selectedClient}
              onChange={(e) => {
                setSelectedClient(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            />
            {showSuggestions && selectedClient && (
              <div className="absolute z-50 w-full border rounded-lg mt-1 max-h-40 overflow-y-auto bg-white shadow-lg">
                {clients
                  .filter((c) =>
                    c.c_name.toLowerCase().includes(selectedClient.toLowerCase())
                  )
                  .map((c) => (
                    <div
                      key={c.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-800"
                      onClick={() => {
                        setSelectedClient(c.c_name);
                        setShowSuggestions(false);
                      }}
                    >
                      {c.c_name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 block"><Label>Status</Label></div>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Initial">Initial</option>
              <option value="Started">Started</option>
              <option value="Completed">Completed</option>
            </Select>
          </div>

          <div>
            <div className="mb-2 block"><Label>Start Date (Optional)</Label></div>
            <Datepicker
              value={s_date}
              onChange={(date) => setSDate(date)}
              weekStart={1}
              inline={false}
            />
          </div>

          <div>
            <div className="mb-2 block"><Label>End Date (Optional)</Label></div>
            <Datepicker
              value={e_date}
              onChange={(date) => setEDate(date)}
              weekStart={1}
              inline={false}
            />
          </div>

          <div className="w-full flex px-8">
            <Button
              onClick={() => {
                setJName("");
                setDescription("");
                setSelectedClient("");
                setSDate(null);
                setEDate(null);
                setStatus("Initial");
              }}
            >
              Clear
            </Button>
            <Button onClick={handleSubmit} className="ml-auto">
              {e_id ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </ModalBody>
      {adis && (
        <Alert className="absolute right-0 top-12" color={t_color}>
          <span className="font-medium">{msg_n}</span> {msg}
        </Alert>
      )}
    </Modal>
  );
}
